/**
 * The cookie notice — a quiet, one-time note on the public pages.
 *
 * WHY A NOTICE AND NOT A CONSENT BANNER (Cookiebot & co.)
 * Caligo sets only strictly necessary cookies (`gamer_id`, `refreshToken` — the
 * sign-in pair) and keeps preferences the player chose (language, sound) in
 * local storage. Under the ePrivacy rules both are exempt from consent: there
 * is nothing optional to accept or refuse, so an Accept/Reject wall would be
 * theatre. What the visitor IS owed is to be told, with a link to the full
 * list — that is this. If analytics or ads are ever added, this must become a
 * real consent banner BEFORE they load (the Cookie Policy says so too).
 *
 * Shown on the website and the auth screens, never over the game or the admin
 * panel (a signed-in player already accepted the Terms that link the policy).
 * Dismissal is remembered per browser; in private mode it simply returns.
 * Vanilla TS like the install banner, because it must outlive SPA routing.
 */
import { t, onLocaleChange } from './i18n';

const KEY = 'caligo:cookie-notice';
const HIDDEN_ON = /^\/(play|admin-panel)(\/|$)/;

let host: HTMLElement | null = null;

export function cookieNoticeDismissed(): boolean {
  try { return localStorage.getItem(KEY) === 'ok'; } catch { return false; }
}

function dismiss(): void {
  try { localStorage.setItem(KEY, 'ok'); } catch { /* private mode: it returns next visit */ }
  host?.remove();
  host = null;
}

function paint(el: HTMLElement): void {
  el.setAttribute('aria-label', t('cookieNotice.label'));
  el.innerHTML = `
    <div class="flex flex-col gap-3 rounded-sm border border-border bg-surface/95 p-4 shadow-lift backdrop-blur sm:flex-row sm:items-center">
      <p class="flex-1 font-body text-sm leading-snug text-text-dim">
        ${t('cookieNotice.body')}
        <a href="/legal/cookie-policy" data-cookie-policy class="whitespace-nowrap text-lamp underline underline-offset-2 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold-bright">${t('cookieNotice.policy')}</a>
      </p>
      <button type="button" data-cookie-ok class="min-h-11 shrink-0 rounded-sm border border-gold bg-crimson px-5 font-label text-sm font-semibold uppercase tracking-widest text-text focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold-bright active:translate-y-px">${t('cookieNotice.ok')}</button>
    </div>`;
  el.querySelector('[data-cookie-ok]')!.addEventListener('click', dismiss);
}

/**
 * Show or hide the notice for the current route. Called after every render;
 * idempotent, and a no-op once the visitor has dismissed it.
 */
export function syncCookieNotice(pathname: string, root: HTMLElement = document.body): void {
  const want = !cookieNoticeDismissed() && !HIDDEN_ON.test(pathname);
  if (!want) {
    host?.remove();
    host = null;
    return;
  }
  if (host?.isConnected) return;
  host = document.createElement('div');
  host.dataset.cookieNotice = '';
  host.setAttribute('role', 'region');
  // z-53: under the install offer (54) and every overlay, over page content.
  host.className = 'fixed inset-x-0 bottom-0 z-[53] p-3 sm:right-auto sm:left-4 sm:w-[30rem]';
  host.style.paddingBottom = 'calc(env(safe-area-inset-bottom, 0px) + 0.75rem)';
  paint(host);
  root.appendChild(host);
}

// A language switch repaints the open notice in the new tongue.
onLocaleChange(() => { if (host) paint(host); });

/** Test seam: forget the mounted node (the DOM is reset between tests). */
export function _resetCookieNotice(): void {
  host?.remove();
  host = null;
}
